import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["margin", "styles", "children", "onDismiss", "variantScreenReaderLabel"];
var _dec, _dec2, _class, _Alert;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Fragment, Component } from 'react';
import ReactDOM from 'react-dom';
import keycode from 'keycode';
import { callRenderProp, withDeterministicId, passthroughProps } from '@instructure/ui-react-utils';
import { CloseButton } from '@instructure/ui-buttons';
import { View } from '@instructure/ui-view';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { IconCheckMarkSolid, IconInfoBorderlessSolid, IconWarningBorderlessSolid, IconNoSolid } from '@instructure/ui-icons';
import { Transition } from '@instructure/ui-motion';
import { logError as error } from '@instructure/console';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
import { jsxs as _jsxs, jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
category: components
---
**/
let Alert = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec(_class = _dec2(_class = (_Alert = class Alert extends Component {
  constructor(props) {
    super(props);
    this._timeouts = [];
    this.srid = void 0;
    this.variantUI = {
      error: IconNoSolid,
      info: IconInfoBorderlessSolid,
      success: IconCheckMarkSolid,
      warning: IconWarningBorderlessSolid
    };
    this.ref = null;
    this.handleRef = el => {
      this.ref = el;
    };
    this.handleTimeout = () => {
      if (this.props.timeout > 0) {
        this._timeouts.push(setTimeout(() => {
          this.close();
        }, this.props.timeout));
      }
    };
    this.onExitTransition = () => {
      if (this.props.onDismiss) {
        this.props.onDismiss();
      }
    };
    this.close = () => {
      this.clearTimeouts();
      this.removeScreenreaderAlert();
      this.setState({
        open: false
      }, () => {
        if (this.props.onDismiss && (this.props.transition === 'none' || this.props.screenReaderOnly)) {
          this.props.onDismiss();
        }
      });
    };
    this.handleKeyUp = event => {
      if (this.props.renderCloseButtonLabel && event.keyCode === keycode.codes.esc) {
        this.close();
      }
    };
    this.srid = this.props.deterministicId();
    this.state = {
      open: true
    };
  }
  clearTimeouts() {
    this._timeouts.forEach(timeout => clearTimeout(timeout));
    this._timeouts = [];
  }
  // duck type for a dom node
  isDOMNode(n) {
    return n && typeof n === 'object' && n.nodeType === 1;
  }
  getLiveRegion() {
    const lr = typeof this.props.liveRegion === 'function' ? this.props.liveRegion() : this.props.liveRegion;
    return this.isDOMNode(lr) ? lr : null;
  }
  initLiveRegion(liveRegion) {
    error(liveRegion.getAttribute('role') === 'alert', `[Alert] live region must have role='alert' set on page load in order to announce content`);
    if (liveRegion) {
      liveRegion.setAttribute('aria-live', this.props.liveRegionPoliteness);
      // indicates what notifications the user agent will trigger when the
      // accessibility tree within a live region is modified.
      // additions: elements are added, text: Text content is added
      liveRegion.setAttribute('aria-relevant', 'additions text');
      liveRegion.setAttribute('aria-atomic', `${this.props.isLiveRegionAtomic}`);
    }
  }
  createScreenreaderContentNode() {
    return _jsxs(ScreenReaderContent, {
      children: [this.props.variantScreenReaderLabel || '', " ", this.props.children]
    });
  }
  createScreenreaderAlert() {
    const liveRegion = this.getLiveRegion();
    if (liveRegion) {
      const div = document.createElement('div');
      div.setAttribute('id', this.srid);
      liveRegion.appendChild(div);
    }
  }
  removeScreenreaderAlert() {
    const liveRegion = this.getLiveRegion();
    if (liveRegion) {
      const div = document.getElementById(this.srid);
      if (div) {
        // Accessibility attributes must be removed for the deletion of the node
        // and then reapplied because JAWS/IE will not respect the
        // "aria-relevant" attribute and read when the node is deleted if
        // the attributes are in place
        liveRegion.removeAttribute('aria-live');
        liveRegion.removeAttribute('aria-relevant');
        liveRegion.removeAttribute('aria-atomic');
        this.initLiveRegion(liveRegion);
      }
    }
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
    const liveRegion = this.getLiveRegion();
    if (liveRegion) {
      this.initLiveRegion(liveRegion);
    }
    this.handleTimeout();
  }
  componentWillUnmount() {
    this.clearTimeouts();
  }
  componentDidUpdate(prevProps) {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
    if (!!this.props.open === false && !!this.props.open !== !!prevProps.open) {
      // this outside world is asking us to close the alert, which needs to
      // take place internally so the transition runs
      this.close();
    }
  }
  renderIcon() {
    const _this$props3 = this.props,
      renderCustomIcon = _this$props3.renderCustomIcon,
      variant = _this$props3.variant,
      styles = _this$props3.styles;
    const Icon = this.variantUI[variant];
    return _jsx("div", {
      css: styles === null || styles === void 0 ? void 0 : styles.icon,
      children: renderCustomIcon ? callRenderProp(renderCustomIcon) : _jsx(Icon, {})
    });
  }
  renderCloseButton() {
    var _this$props$styles;
    const closeButtonLabel = this.props.renderCloseButtonLabel && callRenderProp(this.props.renderCloseButtonLabel);
    return closeButtonLabel ? _jsx("div", {
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.closeButton,
      children: _jsx(CloseButton, {
        onClick: this.close,
        size: "small",
        screenReaderLabel: closeButtonLabel
      })
    }, "closeButton") : null;
  }
  renderAlert() {
    // prevent onDismiss from being passed to the View component
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _this$props4 = this.props,
      margin = _this$props4.margin,
      styles = _this$props4.styles,
      children = _this$props4.children,
      onDismiss = _this$props4.onDismiss,
      variantScreenReaderLabel = _this$props4.variantScreenReaderLabel,
      props = _objectWithoutProperties(_this$props4, _excluded);
    return _jsxs(View, {
      ...passthroughProps({
        ...props
      }),
      as: "div",
      margin: margin,
      css: styles === null || styles === void 0 ? void 0 : styles.alert,
      onKeyUp: this.handleKeyUp,
      elementRef: this.handleRef,
      children: [this.renderIcon(), _jsxs("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.content,
        children: [variantScreenReaderLabel && _jsx("span", {
          css: styles === null || styles === void 0 ? void 0 : styles.variantScreenReaderLabel,
          children: variantScreenReaderLabel
        }), children]
      }), this.renderCloseButton()]
    });
  }
  createScreenReaderPortal(liveRegion) {
    const open = this.state.open;
    return open ? /*#__PURE__*/ReactDOM.createPortal(_jsx("div", {
      id: this.srid,
      children: this.createScreenreaderContentNode()
    }), liveRegion) : null;
  }
  render() {
    const liveRegion = this.getLiveRegion();
    const screenReaderContent = liveRegion ? this.createScreenReaderPortal(liveRegion) : null;
    // Don't render anything if screen reader only
    if (this.props.screenReaderOnly) {
      error(!!this.getLiveRegion(), `[Alert] The 'screenReaderOnly' prop must be used in conjunction with 'liveRegion'.`);
      return screenReaderContent;
    }
    if (this.props.transition === 'none') {
      return this.state.open ? _jsxs(Fragment, {
        children: [screenReaderContent, this.renderAlert()]
      }) : null;
    }
    return _jsxs(Fragment, {
      children: [screenReaderContent, _jsx(Transition, {
        type: this.props.transition,
        transitionOnMount: true,
        in: this.state.open,
        unmountOnExit: true,
        onExited: this.onExitTransition,
        children: this.renderAlert()
      })]
    });
  }
}, _Alert.displayName = "Alert", _Alert.componentId = 'Alert', _Alert.propTypes = propTypes, _Alert.allowedProps = allowedProps, _Alert.defaultProps = {
  variant: 'info',
  margin: 'x-small 0',
  timeout: 0,
  transition: 'fade',
  open: true,
  screenReaderOnly: false,
  liveRegionPoliteness: 'assertive',
  isLiveRegionAtomic: false,
  children: null,
  hasShadow: true
}, _Alert)) || _class) || _class);
export default Alert;
export { Alert };